import React, { useState, useEffect } from 'react';
import api from '../api';

const DISCREPANCY_COLOR = {
  qty_mismatch: '#f59e0b',
  price_mismatch: '#f97316',
  not_on_po: '#8b5cf6',
};

export default function InvoiceReceiving({ invoiceId, onComplete }) {
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lineItems, setLineItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [confirming, setConfirming] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    Promise.all([
      api.get(`/invoices/${invoiceId}`),
      api.get('/inventory/categories'),
    ]).then(([{ data: inv }, { data: cats }]) => {
      setInvoice(inv);
      setLineItems((inv.line_items || []).map(li => ({
        ...li,
        quantity_received: li.quantity ?? '',
        received: true,
        category: li.category || '',
      })));
      setCategories(cats || []);
    }).catch(() => setError('Failed to load invoice')).finally(() => setLoading(false));
  }, [invoiceId]);

  function toggle(id) {
    setLineItems(prev => prev.map(li => li.id === id ? { ...li, received: !li.received } : li));
  }

  function updateQty(id, val) {
    setLineItems(prev => prev.map(li => li.id === id ? { ...li, quantity_received: val } : li));
  }

  function updateCategory(id, val) {
    setLineItems(prev => prev.map(li => li.id === id ? { ...li, category: val } : li));
    // Add to local dropdown list immediately if it's new
    if (val && !categories.includes(val)) {
      setCategories(prev => [...prev, val].sort());
    }
  }

  // Group line items by category for display
  const grouped = lineItems.reduce((acc, li) => {
    const key = li.category || '(Uncategorized)';
    if (!acc[key]) acc[key] = [];
    acc[key].push(li);
    return acc;
  }, {});

  async function confirmReceipt() {
    setConfirming(true);
    setError(null);
    try {
      const received_items = lineItems.map(li => ({
        line_item_id: li.id,
        quantity_received: parseFloat(li.quantity_received) || 0,
        received: li.received,
        category: li.category || null,
        inventory_item_id: li.inventory_item_id || null,
      }));
      await api.post(`/invoices/${invoiceId}/receive`, { received_items });
      setDone(true);
      onComplete?.();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to confirm receipt');
    } finally { setConfirming(false); }
  }

  if (loading) return <div className="loading">Loading invoice...</div>;
  if (error && !invoice) return <div className="alert alert-error">{error}</div>;
  if (done) return (
    <div className="card">
      <div className="card-body" style={{ textAlign: 'center', padding: 40 }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
        <div className="font-semibold" style={{ fontSize: 18, marginBottom: 6 }}>Receipt Confirmed!</div>
        <p className="text-gray text-sm">Inventory has been updated with the received quantities.</p>
      </div>
    </div>
  );

  const hasPO = !!invoice?.purchase_order;
  const hasDiscrepancies = lineItems.some(li => li.discrepancy);
  const receivedCount = lineItems.filter(li => li.received).length;
  const datalistId = `cats-${invoiceId}`;

  return (
    <div>
      {/* Hidden datalist for category autocomplete */}
      <datalist id={datalistId}>
        {categories.map(c => <option key={c} value={c} />)}
      </datalist>

      {/* Header */}
      <div className="card mb-4">
        <div className="card-body" style={{ padding: '12px 16px' }}>
          <div className="flex justify-between items-center">
            <div>
              <span className="font-semibold" style={{ fontSize: 16 }}>{invoice?.vendor_name || 'Invoice'}</span>
              {hasPO
                ? <span className="badge badge-teal" style={{ marginLeft: 8 }}>✓ Matched to PO</span>
                : <span className="badge badge-gray" style={{ marginLeft: 8 }}>No PO on file</span>}
            </div>
            <span className="text-gray text-sm">{new Date(invoice?.scanned_at).toLocaleDateString()}</span>
          </div>
          {hasDiscrepancies && (
            <div className="alert alert-warning" style={{ marginTop: 8, padding: '6px 10px', fontSize: 13 }}>
              ⚠️ Discrepancies found between invoice and purchase order — review before confirming.
            </div>
          )}
        </div>
      </div>

      {/* Line items grouped by category */}
      <div className="card mb-4">
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Line Items ({lineItems.length})</span>
          <div className="flex gap-2">
            <button className="btn btn-ghost btn-sm" style={{ fontSize: 11 }} onClick={() => setLineItems(p => p.map(i => ({ ...i, received: true })))}>Select All</button>
            <button className="btn btn-ghost btn-sm" style={{ fontSize: 11 }} onClick={() => setLineItems(p => p.map(i => ({ ...i, received: false })))}>Deselect All</button>
          </div>
        </div>

        {Object.entries(grouped).map(([cat, items]) => {
          const isOpen = expanded[cat] !== false; // default open
          const totalQty = items.filter(i => i.received).reduce((s, i) => s + (parseFloat(i.quantity_received) || 0), 0);
          const totalCost = items.reduce((s, i) => s + (i.total_price || (i.unit_price * i.quantity) || 0), 0);
          const unit = items[0]?.unit || '';

          return (
            <div key={cat} style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
              {/* Category header row */}
              <div
                onClick={() => setExpanded(p => ({ ...p, [cat]: !isOpen }))}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '10px 16px', cursor: 'pointer',
                  background: 'rgba(255,255,255,0.02)',
                  userSelect: 'none',
                }}
              >
                <span style={{ fontSize: 11, color: '#64748b', transition: 'transform .15s', display: 'inline-block', transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
                <span style={{ fontWeight: 700, fontSize: 13.5, flex: 1 }}>{cat}</span>
                <span className="text-gray text-sm">{items.length} item{items.length > 1 ? 's' : ''}</span>
                {totalQty > 0 && <span className="text-gray text-sm">· {totalQty.toFixed(2)} {unit} received</span>}
                {totalCost > 0 && <span className="text-gray text-sm">· ${totalCost.toFixed(2)}</span>}
              </div>

              {/* Expanded detail rows */}
              {isOpen && (
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th style={{ width: 32 }}>✓</th>
                        <th>Item (from invoice)</th>
                        <th style={{ width: 160 }}>Inventory Category</th>
                        <th>Invoiced</th>
                        <th>Qty Received</th>
                        <th>Unit Price</th>
                        {hasPO && <th>Status</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {items.map(li => (
                        <tr key={li.id} style={{ opacity: li.received ? 1 : 0.45 }}>
                          <td>
                            <input type="checkbox" checked={li.received} onChange={() => toggle(li.id)} style={{ width: 16, height: 16, cursor: 'pointer' }} />
                          </td>
                          <td style={{ fontSize: 13 }}>{li.item_name}</td>
                          <td>
                            <input
                              list={datalistId}
                              value={li.category}
                              onChange={e => updateCategory(li.id, e.target.value)}
                              placeholder="Category…"
                              style={{
                                width: '100%', fontSize: 12,
                                background: 'rgba(18,207,196,0.07)',
                                border: '1px solid rgba(18,207,196,0.25)',
                                borderRadius: 6, padding: '3px 7px', color: 'inherit',
                              }}
                            />
                          </td>
                          <td className="text-sm">{li.quantity} {li.unit || ''}</td>
                          <td>
                            <input
                              type="number" min="0" step="0.01"
                              value={li.quantity_received}
                              onChange={e => updateQty(li.id, e.target.value)}
                              disabled={!li.received}
                              style={{ width: 80, fontSize: 13, background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 6, padding: '3px 6px', color: 'inherit' }}
                            />
                          </td>
                          <td className="text-sm text-gray">
                            {li.unit_price != null ? `$${Number(li.unit_price).toFixed(2)}` : '—'}
                            {li.price_discrepancy != null && (
                              <span style={{ color: DISCREPANCY_COLOR.price_mismatch, fontSize: 11, marginLeft: 4 }}>
                                ({li.price_discrepancy > 0 ? '+' : ''}${Number(li.price_discrepancy).toFixed(2)})
                              </span>
                            )}
                          </td>
                          {hasPO && (
                            <td>
                              {!li.discrepancy && <span className="badge badge-green" style={{ fontSize: 10 }}>✓ Match</span>}
                              {li.discrepancy === 'qty_mismatch' && <span className="badge badge-yellow" style={{ fontSize: 10 }}>Qty off ({li.qty_discrepancy > 0 ? '+' : ''}{li.qty_discrepancy})</span>}
                              {li.discrepancy === 'price_mismatch' && <span style={{ fontSize: 10, background: 'rgba(249,115,22,0.2)', color: '#f97316', padding: '2px 6px', borderRadius: 4 }}>Price off</span>}
                              {li.discrepancy === 'not_on_po' && <span style={{ fontSize: 10, background: 'rgba(139,92,246,0.2)', color: '#a78bfa', padding: '2px 6px', borderRadius: 4 }}>Not on PO</span>}
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {error && <div className="alert alert-error mb-4">{error}</div>}

      {/* Confirm */}
      <div className="card">
        <div className="card-body" style={{ padding: '16px' }}>
          <div className="flex justify-between items-center">
            <div>
              <div className="font-semibold">{receivedCount} of {lineItems.length} items marked received</div>
              <div className="text-gray text-sm">Categories will be created in inventory if they don't exist yet.</div>
            </div>
            <button
              className="btn btn-primary"
              onClick={confirmReceipt}
              disabled={confirming || receivedCount === 0}
              style={{ minWidth: 160 }}
            >
              {confirming ? 'Confirming...' : '✓ Confirm Receipt'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
